import React, { ForwardedRef, forwardRef, memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Select, SelectOption } from '@components/select';
import { COUNTRIES, Country } from '@constants/countries';
import { iso2FlagEmoji } from '@utils/iso-to-flag-emoji';

import { CountrySelectorProps } from './country-selector.type';

export const CountrySelector = memo(
  forwardRef(
    (
      {
        name,
        hasError,
        selectFontLight,
        required,
        initialCountry,
        id,
        disabled,
        readOnly,
        onChange,
        className,
        showDial,
        showSelectedLabel,
        countries,
        ...props
      }: CountrySelectorProps,
      ref: ForwardedRef<HTMLSelectElement>
    ) => {
      const { t } = useTranslation();
      const [selected, setSelected] = useState(initialCountry);
      const [isDropdownOpen, setDropdownOpen] = useState(false);
      const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setDropdownOpen(false);
        setSelected(e.target.value);
        if (onChange) onChange(e);
      };

      const createLabel = useCallback(
        (option: Country) => {
          const dial = showDial ? option.dialCode : '';
          const countryName = showSelectedLabel || isDropdownOpen ? option.label : '';
          const flag = iso2FlagEmoji(option.isoCode);

          const selectedOptionLabel = `${flag} ${countryName} ${dial}`;
          const optionLabel = `${flag} ${option.label} ${dial}`;

          if (selected === option.isoCode) return selectedOptionLabel;
          return optionLabel;
        },
        [selected, showDial, showSelectedLabel, isDropdownOpen]
      );
      return (
        <Select
          {...props}
          ref={ref}
          id={id}
          name={name}
          data-testid='select-country-input'
          hasError={hasError}
          required={required}
          disabled={disabled}
          readOnly={readOnly}
          selectFontLight={selectFontLight}
          value={selected}
          onChange={(e) => handleSelectChange(e as React.ChangeEvent<HTMLSelectElement>)}
          onFocus={() => setDropdownOpen(true)}
          onBlur={() => setDropdownOpen(false)}
          className={className}>
          {!selected && <SelectOption value='' label={t('common:selectCountryLabel')} disabled={!!selected} />}
          {React.useMemo(() => {
            return Object.values(countries ?? COUNTRIES).map((option) => (
              <SelectOption
                key={option.isoCode}
                data-testid={`select-country-option-${option.isoCode}`}
                value={option.isoCode}
                label={createLabel(option)}
              />
            ));
          }, [countries, createLabel])}
        </Select>
      );
    }
  )
);
